// Design tokens for the redesigned BMD student screens.
//
// The three screens the PM mocked up - the section overview, the question, and
// the finish card - are one flow and were drawn with one palette. Kept here
// rather than repeated as literals in each file so that changing the accent, or
// the card radius, is one edit rather than four near-misses.
//
// Deliberately plain objects, not SCSS: these screens live inside the existing
// Classes pages, which already carry the app's global stylesheet, and adding a
// second cascade for four components is how the two end up fighting.

export const BMD_UI = Object.freeze({
  // Page and card surfaces.
  PAGE_BG: "#eef2fb",
  CARD_BG: "#ffffff",
  CARD_RADIUS: 14,
  CARD_BORDER: "1px solid #e3e9f5",

  // Type.
  TITLE: "#16264a",
  BODY: "#5a6b8c",
  MUTED: "#8a97b1",

  // Progress.
  TRACK: "#e2e8f5",
  FILL: "#1f3a6e",
  /** The segment the student is on, between filled and empty. */
  FILL_CURRENT: "#7d9ad0",

  // The one call to action on every screen.
  ACCENT: "#c62644",

  // Answer options and section rows.
  //
  // OPTION_BORDER is the hairline used for dividers, the reason textarea and
  // idle chrome. It is deliberately near-white and must stay that way.
  OPTION_BORDER: "#e3e9f5",

  /**
   * The resting border on anything the student can interact with: the answer
   * tiles, the reason textarea and the confidence cards.
   *
   * It sits between the background and the selected border. Dark enough to
   * read as a defined box on white, which OPTION_BORDER at #e3e9f5 did not,
   * and light enough not to be mistaken for a selection. Selection carries
   * three further signals anyway - 2px instead of 1px, a tinted fill, and the
   * filled letter circle with its tick - so this does not have to approach the
   * navy to stay distinguishable.
   *
   * Its own token because OPTION_BORDER is shared with the divider under the
   * question, where anything this strong reads as a rule rather than a
   * hairline. 
   */
  CONTROL_BORDER: "#5b6f9c",
  /**
   * Resting and selected border widths for those same controls.
   *
   * The resting one is 2px because 1px read as faint whatever colour it
   * was given. Selected stays one step thicker so WIDTH still carries
   * selection: if both were 2px, selection would rest on colour alone.
   */
  CONTROL_BORDER_WIDTH: 2,
  CONTROL_SELECTED_BORDER_WIDTH: 3,
  OPTION_SELECTED_BORDER: "#1f3a6e",
  OPTION_SELECTED_BG: "#f5f8ff",
  LETTER_BG: "#f4f7fd",
  LETTER_BORDER: "#b9c6de",
  /**
   * The A/B/C/D letter on an unselected option.
   *
   * It was MUTED (#8a97b1) on a #f4f7fd circle, which is barely legible. This
   * is dark enough to read at 12px and still nowhere near the selected state,
   * which is not a darker letter but an inverted one: white on a filled navy
   * circle. So making this darker cannot be mistaken for a selection.
   */
  LETTER_TEXT: "#41537a",

  // Status pills on the section list.
  PILL_ACTIVE_BG: "#dce6f8",
  PILL_ACTIVE_TEXT: "#2c4a86",
  PILL_IDLE_BG: "#eef1f6",
  PILL_IDLE_TEXT: "#7a869d",
  PILL_DONE_BG: "#d8f3e3",
  PILL_DONE_TEXT: "#1c6b45",

  // The countdown ring on the question screen.
  RING_TRACK: "#e6ecf7",
  RING_OK: "#1f3a6e",
  RING_WARN: "#e8a33d",
  RING_DANGER: "#c62644",

  // The confidence scale. Amber rather than the app's red: the student is being
  // asked how sure they feel, and there is no wrong answer to colour as one.
  CONFIDENCE_BORDER: "#e8a33d",
  CONFIDENCE_BG: "#fdf4e3",
  CONFIDENCE_TEXT: "#8a5a00",
})

/**
 * Rounded-minutes phrasing for a question count.
 *
 * Takes the seconds-per-question estimate as an argument rather than importing
 * it, so the estimate keeps exactly one home in bmd.constants.
 */
export function approxMinutes(
  questionCount: number,
  secondsEach: number,
): number {
  return Math.max(1, Math.round((questionCount * secondsEach) / 60))
}
